import React, { useState, useRef, useCallback, useTransition } from 'react';
import * as XLSX from 'xlsx';
import {
    ArrowLeft, Search, Upload, X, Loader2, FileSpreadsheet,
    MessageSquare, Mail, Bell, MessageCircle, ChevronDown,
    CheckCircle, XCircle, AlertCircle, Trash2, Hash, Eye, RefreshCw, Tag,
    Columns, HelpCircle, ShieldAlert, Wifi, Zap
} from 'lucide-react';
import AnimatedBackground from './Background';
import { apiFetch } from '../api';
import { useWebSocket } from '../websocket/WebSocketContext';
import { ensureSocketConnection } from '../websocket/socketUtils';
import WhatsAppDetails from './whatsapp/WhatsAppDetails';
import FCMDetails from '../pages/Fcmdetails';
import EmailDetails from '../pages/EmailDetails';
import SMSDetails from '../pages/SMSDetails';

/* ─── per-mode limits ─────────────────────────────────────────────────── */
const MAX_IDS_API = 2000;  // REST API batch cap
const MAX_IDS_SOCKET = 200;  // WebSocket stream hard cap (server-enforced)

/* ─── channel config ──────────────────────────────────────────────────── */
const CHANNELS = {
    sms: {
        label: 'SMS', icon: MessageSquare,
        color: 'from-green-500 to-emerald-500', ring: 'focus:ring-green-500',
        border: 'border-green-500/40', bg: 'bg-green-500/10', text: 'text-green-300',
        endpoint: '/get/sms/logs/by-id',
        wsDestination: '/app/get/sms/logs/by-id',
        wsTopic: (loginId) => `/topic/get/sms/logs/by-id/${loginId}`,
    },
    email: {
        label: 'Email', icon: Mail,
        color: 'from-blue-500 to-cyan-500', ring: 'focus:ring-blue-500',
        border: 'border-blue-500/40', bg: 'bg-blue-500/10', text: 'text-blue-300',
        endpoint: '/get/email/logs/by-id',
        wsDestination: '/app/get/email/logs/by-id',
        wsTopic: (loginId) => `/topic/get/email/logs/by-id/${loginId}`,
    },
    fcm: {
        label: 'FCM / Push', icon: Bell,
        color: 'from-orange-500 to-amber-500', ring: 'focus:ring-orange-500',
        border: 'border-orange-500/40', bg: 'bg-orange-500/10', text: 'text-orange-300',
        endpoint: '/get/fcm/logs/by-id',
        wsDestination: '/app/get/fcm/logs/by-id',
        wsTopic: (loginId) => `/topic/get/fcm/logs/by-id/${loginId}`,
    },
    whatsapp: {
        label: 'WhatsApp', icon: MessageCircle,
        color: 'from-teal-500 to-green-400', ring: 'focus:ring-teal-500',
        border: 'border-teal-500/40', bg: 'bg-teal-500/10', text: 'text-teal-300',
        endpoint: '/get/whatsapp/logs/by-id',
        wsDestination: '/app/get/whatsapp/logs/by-id',
        wsTopic: (loginId) => `/topic/get/whatsapp/logs/by-id/${loginId}`,
    },
};

const cleanId = (s) => s.trim().replace(/^["']|["']$/g, '');

function extractIdsFromRows(rows, colName) {
    if (!rows.length || !colName) return [];
    const headers = rows[0].map(String);
    const hIdx = headers.indexOf(colName);
    if (hIdx === -1) return [];
    return rows.slice(1).map(r => String(r[hIdx] ?? '').trim()).filter(Boolean);
}

/* ─── LimitModal ──────────────────────────────────────────────────────── */
/**
 * Two visual modes:
 *   deliveryMode === 'socket' → HARD STOP  (red/Wifi icon, no Proceed button,
 *                                           explain 500-ID cap, suggest API mode)
 *   deliveryMode === 'api'    → SOFT CAP   (amber/ShieldAlert icon, can Proceed
 *                                           with first 100 IDs)
 */
function LimitModal({ isOpen, totalCount, onProceed, onCancel, channel, deliveryMode }) {
    const ch = CHANNELS[channel];
    const isSocket = deliveryMode === 'socket';

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-[999] flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)' }}
            onClick={onCancel}
        >
            <div
                className="relative w-full max-w-md rounded-2xl overflow-hidden shadow-2xl border border-white/10"
                style={{ background: 'rgba(10, 18, 38, 0.97)' }}
                onClick={e => e.stopPropagation()}
            >
                {/* accent bar — amber for socket soft-cap, channel colour for API soft-cap */}
                <div className={`h-1 w-full ${isSocket ? 'bg-gradient-to-r from-amber-500 to-orange-400' : `bg-gradient-to-r ${ch.color}`}`} />

                {/* close */}
                <button
                    onClick={onCancel}
                    className="absolute top-4 right-4 p-1.5 rounded-lg text-white/30 hover:text-white hover:bg-white/10 transition"
                >
                    <X className="w-4 h-4" />
                </button>

                <div className="p-8">

                    {/* ── SOCKET: soft cap — proceed with first 500 ──────── */}
                    {isSocket ? (
                        <>
                            {/* Icon */}
                            <div className="flex justify-center mb-6">
                                <div className="relative">
                                    <div className="w-24 h-24 rounded-full flex items-center justify-center border border-amber-500/25"
                                        style={{ background: 'rgba(245,158,11,0.08)' }}>
                                        <div className="w-16 h-16 rounded-full flex items-center justify-center border border-amber-400/30"
                                            style={{ background: 'rgba(245,158,11,0.15)' }}>
                                            <Wifi className="w-7 h-7 text-amber-300" />
                                        </div>
                                    </div>
                                    <div className="absolute -bottom-1 -right-2 px-2 py-0.5 rounded-full text-[10px] font-black border border-amber-500/40 text-amber-200"
                                        style={{ background: 'rgba(245,158,11,0.28)' }}>
                                        {totalCount.toLocaleString()}
                                    </div>
                                </div>
                            </div>

                            <h2 className="text-white text-xl font-bold text-center mb-1">Stream Limit Exceeded</h2>
                            <p className="text-white/40 text-xs text-center uppercase tracking-widest mb-5 font-medium">
                                Socket Mode · Max {MAX_IDS_SOCKET.toLocaleString()} IDs
                            </p>

                            {/* Explanation */}
                            <div className="rounded-xl p-4 mb-4 border border-amber-500/20 text-center"
                                style={{ background: 'rgba(245,158,11,0.07)' }}>
                                <p className="text-white/85 text-sm leading-relaxed">
                                    You provided{' '}
                                    <span className="text-amber-300 font-bold">{totalCount.toLocaleString()} IDs</span>,
                                    but <span className="font-semibold text-white">Stream mode</span> supports a
                                    maximum of{' '}
                                    <span className="text-amber-300 font-bold">{MAX_IDS_SOCKET.toLocaleString()} IDs</span>{' '}
                                    per search.
                                </p>
                                <p className="text-white/50 text-xs mt-2 leading-relaxed">
                                    Proceeding will stream the first{' '}
                                    <span className="text-amber-300 font-semibold">{MAX_IDS_SOCKET.toLocaleString()} IDs</span>.
                                    Switch to API mode for larger batches.
                                </p>
                            </div>

                            {/* Switch-to-API suggestion */}
                            <div className="rounded-xl p-4 mb-5 border border-indigo-500/25"
                                style={{ background: 'rgba(99,102,241,0.08)' }}>
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center shrink-0 mt-0.5">
                                        <Zap className="w-4 h-4 text-indigo-300" />
                                    </div>
                                    <div>
                                        <p className="text-indigo-200 text-sm font-semibold mb-1">Need more than {MAX_IDS_SOCKET.toLocaleString()}?</p>
                                        <p className="text-white/50 text-xs leading-relaxed">
                                            Switch to <span className="text-white/80 font-semibold">API mode</span> in the dashboard
                                            settings to search up to{' '}
                                            <span className="text-white/80 font-semibold">{MAX_IDS_API.toLocaleString()} IDs</span>{' '}
                                            per request.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Stats */}
                            <div className="grid grid-cols-3 gap-2 mb-6">
                                <div className="rounded-xl p-3 text-center border border-white/10"
                                    style={{ background: 'rgba(255,255,255,0.04)' }}>
                                    <p className="text-white/40 text-[10px] uppercase tracking-wide mb-1">Your IDs</p>
                                    <p className="text-amber-300 font-bold text-base">{totalCount.toLocaleString()}</p>
                                </div>
                                <div className="rounded-xl p-3 text-center border border-amber-500/20"
                                    style={{ background: 'rgba(245,158,11,0.08)' }}>
                                    <p className="text-amber-400/70 text-[10px] uppercase tracking-wide mb-1">Will skip</p>
                                    <p className="text-amber-300 font-bold text-base">{(totalCount - MAX_IDS_SOCKET).toLocaleString()}</p>
                                </div>
                                <div className={`rounded-xl p-3 text-center border ${ch.border}`}
                                    style={{ background: 'rgba(255,255,255,0.05)' }}>
                                    <p className={`${ch.text} opacity-70 text-[10px] uppercase tracking-wide mb-1`}>Will stream</p>
                                    <p className={`${ch.text} font-bold text-base`}>{MAX_IDS_SOCKET.toLocaleString()}</p>
                                </div>
                            </div>

                            {/* Cancel + Proceed */}
                            <div className="flex gap-3">
                                <button
                                    onClick={onCancel}
                                    className="flex-1 py-3 rounded-xl font-semibold text-sm text-white/60 hover:text-white border border-white/15 hover:border-white/30 transition"
                                    style={{ background: 'rgba(255,255,255,0.06)' }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={onProceed}
                                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm text-white bg-gradient-to-r ${ch.color} hover:opacity-90 transition shadow-lg`}
                                >
                                    <Search className="w-4 h-4" />
                                    Stream first {MAX_IDS_SOCKET.toLocaleString()}
                                </button>
                            </div>
                        </>
                    ) : (
                        /* ── API: soft cap — can proceed with first 100 ── */
                        <>
                            {/* Icon */}
                            <div className="flex justify-center mb-6">
                                <div className="relative">
                                    <div className="w-24 h-24 rounded-full flex items-center justify-center border border-amber-500/20"
                                        style={{ background: 'rgba(245,158,11,0.08)' }}>
                                        <div className="w-16 h-16 rounded-full flex items-center justify-center border border-amber-400/25"
                                            style={{ background: 'rgba(245,158,11,0.14)' }}>
                                            <ShieldAlert className="w-7 h-7 text-amber-300" />
                                        </div>
                                    </div>
                                    <div className="absolute -bottom-1 -right-2 px-2 py-0.5 rounded-full text-[10px] font-black border border-amber-500/40 text-amber-200"
                                        style={{ background: 'rgba(245,158,11,0.25)' }}>
                                        {totalCount.toLocaleString()}
                                    </div>
                                </div>
                            </div>

                            <h2 className="text-white text-xl font-bold text-center mb-1">Too Many IDs</h2>
                            <p className="text-white/40 text-xs text-center uppercase tracking-widest mb-5 font-medium">
                                Limit Exceeded
                            </p>

                            <div className="rounded-xl p-4 mb-5 border border-amber-500/20 text-center"
                                style={{ background: 'rgba(245,158,11,0.07)' }}>
                                <p className="text-white/80 text-sm leading-relaxed">
                                    Your file contains{' '}
                                    <span className="text-amber-300 font-bold">{totalCount.toLocaleString()} IDs</span>,
                                    but the maximum allowed per API search is{' '}
                                    <span className="text-white font-bold">{MAX_IDS_API.toLocaleString()}</span>.
                                </p>
                                <p className="text-white/50 text-xs mt-2 leading-relaxed">
                                    Proceeding will use only the first{' '}
                                    <span className="text-amber-300 font-semibold">{MAX_IDS_API.toLocaleString()} IDs</span>{' '}
                                    from your file.
                                </p>
                            </div>

                            {/* Stats row */}
                            <div className="grid grid-cols-3 gap-2 mb-6">
                                <div className="rounded-xl p-3 text-center border border-white/8"
                                    style={{ background: 'rgba(255,255,255,0.04)' }}>
                                    <p className="text-white/40 text-[10px] uppercase tracking-wide mb-1">Total found</p>
                                    <p className="text-white font-bold text-base">{totalCount.toLocaleString()}</p>
                                </div>
                                <div className="rounded-xl p-3 text-center border border-amber-500/20"
                                    style={{ background: 'rgba(245,158,11,0.08)' }}>
                                    <p className="text-amber-400/70 text-[10px] uppercase tracking-wide mb-1">Will skip</p>
                                    <p className="text-amber-300 font-bold text-base">{(totalCount - MAX_IDS_API).toLocaleString()}</p>
                                </div>
                                <div className={`rounded-xl p-3 text-center border ${ch.border}`}
                                    style={{ background: 'rgba(255,255,255,0.05)' }}>
                                    <p className={`${ch.text} opacity-70 text-[10px] uppercase tracking-wide mb-1`}>Will search</p>
                                    <p className={`${ch.text} font-bold text-base`}>{MAX_IDS_API.toLocaleString()}</p>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-3">
                                <button
                                    onClick={onCancel}
                                    className="flex-1 py-3 rounded-xl font-semibold text-sm text-white/60 hover:text-white border border-white/15 hover:border-white/30 transition"
                                    style={{ background: 'rgba(255,255,255,0.06)' }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={onProceed}
                                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm text-white bg-gradient-to-r ${ch.color} hover:opacity-90 transition shadow-lg`}
                                >
                                    <Search className="w-4 h-4" />
                                    Proceed with {MAX_IDS_API.toLocaleString()}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ─── StatusBadge ─────────────────────────────────────────────────────── */
function StatusBadge({ status }) {
    if (!status) return null;
    const s = String(status).toUpperCase();
    const cfg = s === 'SUCCESS'
        ? { cls: 'bg-green-500/25 text-green-200 border-green-400/50', Icon: CheckCircle }
        : s === 'FAILED'
            ? { cls: 'bg-red-500/25 text-red-200 border-red-400/50', Icon: XCircle }
            : { cls: 'bg-yellow-500/25 text-yellow-200 border-yellow-400/50', Icon: AlertCircle };
    return (
        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${cfg.cls}`}>
            <cfg.Icon className="w-3.5 h-3.5" />{s}
        </span>
    );
}

/* ─── JsonViewer ──────────────────────────────────────────────────────── */
function JsonViewer({ data, depth = 0 }) {
    const [open, setOpen] = useState(depth < 2);
    if (data === null || data === undefined) return <span className="text-white/40 italic">null</span>;
    if (typeof data !== 'object') return <span className="text-emerald-300 break-all">{String(data)}</span>;
    const isArr = Array.isArray(data);
    const entries = isArr ? data.map((v, i) => [i, v]) : Object.entries(data);
    if (entries.length === 0) return <span className="text-white/40">{isArr ? '[]' : '{}'}</span>;
    return (
        <div className="font-mono text-xs">
            <button onClick={() => setOpen(!open)} className="text-white/60 hover:text-white mr-1">{open ? '▾' : '▸'}</button>
            <span className="text-white/50">{isArr ? '[' : '{'}</span>
            {!open && <span className="text-white/40 cursor-pointer" onClick={() => setOpen(true)}> {entries.length} items… </span>}
            {open && (
                <div className="pl-4 border-l border-white/10 mt-1 space-y-1">
                    {entries.map(([k, v]) => (
                        <div key={k} className="flex gap-2 items-start">
                            <span className="text-sky-300 shrink-0">{isArr ? `[${k}]` : `"${k}"`}:</span>
                            <JsonViewer data={v} depth={depth + 1} />
                        </div>
                    ))}
                </div>
            )}
            <span className="text-white/50">{isArr ? ']' : '}'}</span>
        </div>
    );
}

/* ─── ResultCard ──────────────────────────────────────────────────────── */
function ResultCard({ item, channel, onViewDetails }) {
    const ch = CHANNELS[channel];
    const [expanded, setExpanded] = useState(false);
    const id = item._id || item.id || '—';
    const status = item.STATUS || item.status;

    return (
        <div className="rounded-xl overflow-hidden border border-white/15 shadow-lg"
            style={{ background: 'rgba(10, 20, 40, 0.75)', backdropFilter: 'blur(12px)' }}>
            <div className={`h-1 bg-gradient-to-r ${ch.color}`} />
            <div className="p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className={`p-2 rounded-lg ${ch.bg} shrink-0`}>
                            <ch.icon className={`w-4 h-4 ${ch.text}`} />
                        </div>
                        <div className="min-w-0">
                            <p className="text-white/50 text-[10px] uppercase tracking-widest font-semibold">Document ID</p>
                            <p className="text-white font-mono text-sm break-all font-medium">{id}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 flex-wrap">
                        <StatusBadge status={status} />
                        <button
                            onClick={() => onViewDetails(item)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition hover:opacity-80 ${ch.bg} ${ch.text} border ${ch.border}`}>
                            <Eye className="w-3.5 h-3.5" /> Details
                        </button>
                        <button
                            onClick={() => setExpanded(!expanded)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/15 text-white/80 hover:text-white rounded-lg transition text-xs font-semibold border border-white/10">
                            <Hash className="w-3.5 h-3.5" /> JSON
                            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                        </button>
                    </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-3">
                    {(item.date || item.creation_date) && (
                        <span className="flex items-center gap-1 text-xs text-white/70 bg-white/8 border border-white/10 rounded-full px-2.5 py-1">
                            📅 <span className="text-white/90">{item.date || item.creation_date}</span>
                        </span>
                    )}
                    {(item.creation_time_str || item.createdAt) && (
                        <span className="flex items-center gap-1 text-xs text-white/70 bg-white/8 border border-white/10 rounded-full px-2.5 py-1">
                            ⏰ <span className="text-white/90">{item.creation_time_str || item.createdAt}</span>
                        </span>
                    )}
                    {(item.appName || item.app_name) && (
                        <span className="flex items-center gap-1 text-xs text-white/70 bg-white/8 border border-white/10 rounded-full px-2.5 py-1">
                            🏷 <span className="text-white/90 font-medium">{item.appName || item.app_name}</span>
                        </span>
                    )}
                    {(item.smsVendor || item.vendor || item.vendorName) && (
                        <span className="flex items-center gap-1 text-xs text-white/70 bg-white/8 border border-white/10 rounded-full px-2.5 py-1">
                            🔌 <span className="text-white/90">{item.smsVendor || item.vendor || item.vendorName}</span>
                        </span>
                    )}
                    {item.subject && (
                        <span className="flex items-center gap-1 text-xs text-white/70 bg-white/8 border border-white/10 rounded-full px-2.5 py-1">
                            ✉️ <span className="text-white/90">{item.subject}</span>
                        </span>
                    )}
                    {item.title && (
                        <span className="flex items-center gap-1 text-xs text-white/70 bg-white/8 border border-white/10 rounded-full px-2.5 py-1">
                            🔔 <span className="text-white/90">{item.title}</span>
                        </span>
                    )}
                    {item.mobileNo && (
                        <span className="flex items-center gap-1 text-xs text-white/70 bg-white/8 border border-white/10 rounded-full px-2.5 py-1">
                            📱 <span className="text-white/90">{item.mobileNo}</span>
                        </span>
                    )}
                </div>
                {expanded && (
                    <div className="mt-4 rounded-xl overflow-hidden border border-white/10"
                        style={{ background: 'rgba(0,0,0,0.5)' }}>
                        <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
                            <span className="text-white/40 text-[10px] font-mono uppercase tracking-widest">Raw JSON</span>
                            <button onClick={() => setExpanded(false)} className="text-white/30 hover:text-white transition">
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                        <div className="p-3 max-h-72 overflow-y-auto">
                            <JsonViewer data={item} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════ */
export default function NotificationSearchById({ onBack, setError, loginId, deliveryMode }) {
    const { client } = useWebSocket();

    const [channel, setChannel] = useState('sms');
    const ch = CHANNELS[channel];

    /* active limit depends on current delivery mode */
    const activeLimit = deliveryMode === 'socket' ? MAX_IDS_SOCKET : MAX_IDS_API;

    const [inputMode, setInputMode] = useState('single');
    const [singleId, setSingleId] = useState('');
    const [tags, setTags] = useState([]);
    const [tagInput, setTagInput] = useState('');
    const tagRef = useRef(null);

    /* ── Excel state ─────────────────────────────────────────────── */
    const [excelFile, setExcelFile] = useState(null);
    const [excelRows, setExcelRows] = useState([]);
    const [excelHeaders, setExcelHeaders] = useState([]);
    const [excelCol, setExcelCol] = useState('');
    const [excelIds, setExcelIds] = useState([]);
    const [colConfirmed, setColConfirmed] = useState(false);
    const [excelLoading, setExcelLoading] = useState(false);
    const fileInputRef = useRef(null);

    /* ── Limit-exceeded modal state ──────────────────────────────── */
    const [limitModal, setLimitModal] = useState({ open: false, totalCount: 0, pendingPayload: null });

    const [, startTransition] = useTransition();

    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);

    /* ── Reset all state ─────────────────────────────────────────── */
    const resetAll = useCallback(() => {
        setSingleId('');
        setTags([]);
        setTagInput('');
        setExcelFile(null);
        setExcelRows([]);
        setExcelHeaders([]);
        setExcelCol('');
        setExcelIds([]);
        setColConfirmed(false);
        setResults([]);
        setSearched(false);
        setErrorMsg('');
        setSelectedItem(null);
        setLimitModal({ open: false, totalCount: 0, pendingPayload: null });
        if (fileInputRef.current) fileInputRef.current.value = '';
    }, []);

    const handleChannelChange = useCallback((key) => {
        if (key === channel) return;
        setChannel(key);
        startTransition(() => resetAll());
    }, [channel, resetAll]);

    const handleModeChange = useCallback((key) => {
        setInputMode(key);
        setErrorMsg('');
    }, []);

    /* ── Parse Excel ─────────────────────────────────────────────── */
    const parseExcel = useCallback((file) => {
        setExcelLoading(true);
        setExcelRows([]);
        setExcelHeaders([]);
        setExcelCol('');
        setExcelIds([]);
        setColConfirmed(false);

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const wb = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
                if (!rows.length) throw new Error('Empty sheet');

                const headers = rows[0].map(String);
                const autoCol = headers.find(h => /^id$/i.test(h.trim())) || '';

                startTransition(() => {
                    setExcelHeaders(headers);
                    setExcelRows(rows);
                    setExcelCol(autoCol);
                    if (autoCol) {
                        setExcelIds(extractIdsFromRows(rows, autoCol));
                        setColConfirmed(true);
                    }
                });
            } catch (err) {
                setErrorMsg('Failed to parse Excel: ' + err.message);
            } finally {
                setExcelLoading(false);
            }
        };
        reader.onerror = () => { setErrorMsg('Failed to read file.'); setExcelLoading(false); };
        reader.readAsArrayBuffer(file);
    }, []);

    const handleFileChange = useCallback((e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setExcelFile(file);
        parseExcel(file);
    }, [parseExcel]);

    const handleColSelect = useCallback((colName) => {
        setExcelCol(colName);
        startTransition(() => { setColConfirmed(false); setExcelIds([]); });
    }, []);

    const confirmColumn = useCallback(() => {
        if (!excelCol || !excelRows.length) return;
        const ids = extractIdsFromRows(excelRows, excelCol);
        if (!ids.length) { setErrorMsg('No IDs found in this column.'); return; }
        startTransition(() => { setExcelIds(ids); setColConfirmed(true); setErrorMsg(''); });
    }, [excelCol, excelRows]);

    /* ── Tags ────────────────────────────────────────────────────── */
    const addTag = useCallback((val) => {
        const parts = val.split(/[\s,\n]+/).map(cleanId).filter(Boolean);
        if (!parts.length) return;
        setTags(prev => [...new Set([...prev, ...parts])]);
        setTagInput('');
    }, []);

    const handleTagKey = useCallback((e) => {
        if (['Enter', ',', ' ', 'Tab'].includes(e.key)) { e.preventDefault(); addTag(tagInput); }
        else if (e.key === 'Backspace' && !tagInput) setTags(prev => prev.slice(0, -1));
    }, [addTag, tagInput]);

    const handleTagPaste = useCallback((e) => { e.preventDefault(); addTag(e.clipboardData.getData('text')); }, [addTag]);

    /* ── Core search ────────────────────────────────────────────── */
    const executeSearch = useCallback(async (payload, channelKey) => {
        const cfg = CHANNELS[channelKey];

        if (deliveryMode === 'socket') {
            try { await ensureSocketConnection(client); }
            catch {
                setErrorMsg('Socket connection failed. Please check your connection.');
                setIsLoading(false); setSearched(true); return;
            }

            const topic = cfg.wsTopic(loginId);

            let subscription = null;
            const timeoutHandle = setTimeout(() => {
                try { subscription?.unsubscribe(); } catch { }
                setErrorMsg('Request timed out. Please try again.');
                setResults([]);
                setIsLoading(false);
                setSearched(true);
            }, 90_000);

            subscription = client.subscribe(topic, (msg) => {
                clearTimeout(timeoutHandle);
                try {
                    const raw = JSON.parse(msg.body);
                    const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
                    if (data?.STATUS === 'SUCCESS') {
                        const d = data.DATA;
                        setResults(Array.isArray(d) ? d : d ? [d] : []);
                    } else {
                        setErrorMsg(data?.MESSAGE || 'No data found.');
                        setResults([]);
                    }
                } catch {
                    setErrorMsg('Failed to parse server response.');
                    setResults([]);
                } finally {
                    try { subscription?.unsubscribe(); } catch { }
                    setIsLoading(false);
                    setSearched(true);
                }
            });

            await new Promise(r => setTimeout(r, 50));

            console.log(JSON.stringify(payload));

            try {
                client.publish({
                    destination: cfg.wsDestination,
                    body: JSON.stringify(payload),
                });
            } catch (publishErr) {
                clearTimeout(timeoutHandle);
                try { subscription?.unsubscribe(); } catch { }
                setErrorMsg('Failed to send request: ' + publishErr.message);
                setResults([]);
                setIsLoading(false);
                setSearched(true);
            }

        } else {
            try {
                const res = await apiFetch(cfg.endpoint, { method: 'POST', body: JSON.stringify(payload) });
                const data = await res.json();
                if (data.STATUS === 'SUCCESS') {
                    const d = data.DATA;
                    setResults(Array.isArray(d) ? d : d ? [d] : []);
                } else {
                    setErrorMsg(data.MESSAGE || 'No data found.');
                    setResults([]);
                }
            } catch (e) {
                setErrorMsg('Network error: ' + e.message);
                setResults([]);
            } finally {
                setIsLoading(false); setSearched(true);
            }
        }
    }, [client, deliveryMode, loginId]);

    /* ── Build payload, check limit per mode, maybe show modal ───── */
    const handleSearch = useCallback(async () => {
        setErrorMsg(''); setResults([]); setSearched(false);

        let payload = {};
        let rawIds = [];

        if (inputMode === 'single') {
            const id = cleanId(singleId);
            if (!id) { setErrorMsg('Please enter an ID.'); return; }
            payload = { id };
        } else if (inputMode === 'multi') {
            if (!tags.length) { setErrorMsg('Add at least one ID.'); return; }
            rawIds = tags;
        } else {
            if (!excelFile) { setErrorMsg('Please upload an Excel file.'); return; }
            if (!excelCol) { setErrorMsg('Please select a column.'); return; }
            if (!colConfirmed || !excelIds.length) { setErrorMsg('Please confirm the column selection first.'); return; }
            rawIds = excelIds;
        }

        /* ── limit gate: different cap & behaviour per mode ──────── */
        if (rawIds.length > 0) {
            if (rawIds.length > activeLimit) {
                const isSocket = deliveryMode === 'socket';
                setLimitModal({
                    open: true,
                    totalCount: rawIds.length,
                    // Both modes can proceed — socket caps at 500, API caps at MAX_IDS_API
                    pendingPayload: isSocket
                        ? { ids: rawIds.slice(0, MAX_IDS_SOCKET).join(',') }
                        : { ids: rawIds.slice(0, MAX_IDS_API).join(',') },
                });
                return;
            }
            payload = { ids: rawIds.join(',') };
        }

        setIsLoading(true);
        await executeSearch(payload, channel);
    }, [inputMode, singleId, tags, excelFile, excelCol, colConfirmed, excelIds, channel, activeLimit, deliveryMode, executeSearch]);

    /* ── Modal: Proceed (both modes — socket first 500, API first MAX_IDS_API) */
    const handleLimitProceed = useCallback(async () => {
        const payload = limitModal.pendingPayload;
        setLimitModal({ open: false, totalCount: 0, pendingPayload: null });
        if (!payload) return;
        setIsLoading(true);
        await executeSearch(payload, channel);
    }, [limitModal, channel, executeSearch]);

    /* ── Modal: Cancel / dismiss ─────────────────────────────────── */
    const handleLimitCancel = useCallback(() => {
        setLimitModal({ open: false, totalCount: 0, pendingPayload: null });
    }, []);

    /* ── Detail modals ───────────────────────────────────────────── */
    const renderDetails = () => {
        if (!selectedItem) return null;
        const close = () => setSelectedItem(null);
        if (channel === 'sms') return <SMSDetails selectedSms={selectedItem} onClose={close} setError={setError} loginId={loginId} deliveryMode={deliveryMode} />;
        if (channel === 'email') return <EmailDetails email={selectedItem} onClose={close} setError={setError} loginId={loginId} deliveryMode={deliveryMode} />;
        if (channel === 'fcm') return <FCMDetails selectedNotification={selectedItem} onClose={close} />;
        if (channel === 'whatsapp') return <WhatsAppDetails selectedWp={selectedItem} onClose={close} setError={setError} loginId={loginId} deliveryMode={deliveryMode} />;
        return null;
    };

    /* ── Mode pill ───────────────────────────────────────────────── */
    const ModePill = () => (
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border ${deliveryMode === 'socket'
            ? 'bg-green-500/20 border-green-400/40 text-green-200'
            : 'bg-indigo-500/20 border-indigo-400/40 text-indigo-200'
            }`}>
            <span className={`w-2 h-2 rounded-full ${deliveryMode === 'socket' ? 'bg-green-400 animate-pulse' : 'bg-indigo-400'}`} />
            {deliveryMode === 'socket' ? 'Stream' : 'API'}
        </div>
    );

    /* ═══════════════════════════════════════════════════════════════ */
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 to-[#649EE8]">
            <AnimatedBackground />

            {/* Limit modal — mode-aware (hard stop for socket, soft cap for API) */}
            <LimitModal
                isOpen={limitModal.open}
                totalCount={limitModal.totalCount}
                onProceed={handleLimitProceed}
                onCancel={handleLimitCancel}
                channel={channel}
                deliveryMode={deliveryMode}
            />

            {/* HEADER */}
            <header className="sticky top-0 z-40 border-b border-white/15"
                style={{ background: 'rgba(10, 15, 30, 0.80)', backdropFilter: 'blur(16px)' }}>
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <button onClick={onBack}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white/80 hover:text-white border border-white/15 hover:border-white/30 transition"
                            style={{ background: 'rgba(255,255,255,0.08)' }}>
                            <ArrowLeft className="w-4 h-4" /><span className="text-sm font-medium">Back</span>
                        </button>
                        <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 bg-gradient-to-br ${ch.color} rounded-full flex items-center justify-center shadow-lg`}>
                                <Search className="w-4 h-4 text-white" />
                            </div>
                            <div>
                                <h1 className="text-base font-bold text-white leading-tight">Search by ID</h1>
                                <p className="text-white/40 text-xs">Notification Log Lookup</p>
                            </div>
                        </div>
                        <ModePill />
                    </div>
                </div>
            </header>

            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

                {/* CHANNEL SELECTOR */}
                <div className="rounded-2xl border border-white/15 p-5"
                    style={{ background: 'rgba(10, 20, 40, 0.70)', backdropFilter: 'blur(12px)' }}>
                    <p className="text-white/50 text-[10px] uppercase tracking-widest mb-4 font-semibold">Notification Channel</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {Object.entries(CHANNELS).map(([key, cfg]) => (
                            <button key={key} onClick={() => handleChannelChange(key)}
                                className={`relative flex flex-col items-center gap-2 py-4 px-3 rounded-xl border-2 transition-all duration-200
                                    ${channel === key
                                        ? `border-transparent bg-gradient-to-br ${cfg.color} shadow-xl scale-[1.03]`
                                        : 'border-white/10 hover:border-white/20 hover:scale-[1.01]'}`}
                                style={channel !== key ? { background: 'rgba(255,255,255,0.05)' } : {}}>
                                <cfg.icon className={`w-5 h-5 ${channel === key ? 'text-white' : cfg.text}`} />
                                <span className={`text-sm font-semibold ${channel === key ? 'text-white' : 'text-white/70'}`}>{cfg.label}</span>
                                {channel === key && <span className="absolute top-2 right-2 w-2 h-2 bg-white rounded-full shadow-lg" />}
                            </button>
                        ))}
                    </div>
                </div>

                {/* INPUT MODE TABS */}
                <div className="rounded-2xl border border-white/15 overflow-hidden"
                    style={{ background: 'rgba(10, 20, 40, 0.70)', backdropFilter: 'blur(12px)' }}>
                    <div className="flex border-b border-white/10">
                        {[
                            { key: 'single', label: 'Single ID', icon: Hash },
                            { key: 'multi', label: 'Multiple IDs', icon: Tag },
                            { key: 'excel', label: 'Excel Upload', icon: FileSpreadsheet },
                        ].map(({ key, label, icon: Icon }) => (
                            <button key={key} onClick={() => handleModeChange(key)}
                                className={`flex-1 flex items-center justify-center gap-2 py-3.5 text-sm font-semibold transition-all duration-200
                                    ${inputMode === key ? `${ch.text} border-b-2 border-current` : 'text-white/40 hover:text-white/70'}`}
                                style={inputMode === key ? { background: 'rgba(255,255,255,0.04)' } : {}}>
                                <Icon className="w-4 h-4" />
                                <span className="hidden sm:inline">{label}</span>
                            </button>
                        ))}
                    </div>

                    <div className="p-6">

                        {/* SINGLE */}
                        {inputMode === 'single' && (
                            <div>
                                <label className="block text-white/80 text-sm font-semibold mb-2">{ch.label} Document ID</label>
                                <div className="relative">
                                    <Hash className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                                    <input type="text" value={singleId}
                                        onChange={e => setSingleId(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && handleSearch()}
                                        placeholder="Paste document _id here…"
                                        className={`w-full pl-10 pr-10 py-3 rounded-xl text-white placeholder-white/25 focus:outline-none focus:ring-2 ${ch.ring} transition text-sm border border-white/15`}
                                        style={{ background: 'rgba(255,255,255,0.07)' }} />
                                    {singleId && (
                                        <button onClick={() => setSingleId('')}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white transition">
                                            <X className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                                <p className="text-white/30 text-xs mt-2">
                                    Press <kbd className="bg-white/10 text-white/60 px-1.5 py-0.5 rounded text-[10px]">Enter</kbd> to search
                                </p>
                            </div>
                        )}

                        {/* MULTI */}
                        {inputMode === 'multi' && (
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-white/80 text-sm font-semibold">
                                        IDs — type / paste, press{' '}
                                        <kbd className="bg-white/10 text-white/60 px-1.5 py-0.5 rounded text-[10px]">Enter</kbd> or comma to add
                                    </label>
                                    {/* live limit hint */}
                                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${deliveryMode === 'socket'
                                        ? 'text-green-300 bg-green-500/10 border-green-500/30'
                                        : 'text-indigo-300 bg-indigo-500/10 border-indigo-500/30'
                                        }`}>
                                        max {activeLimit.toLocaleString()}
                                    </span>
                                </div>
                                <div className={`min-h-[96px] p-3 rounded-xl focus-within:ring-2 ${ch.ring} transition cursor-text border border-white/15`}
                                    style={{ background: 'rgba(255,255,255,0.07)' }}
                                    onClick={() => tagRef.current?.focus()}>
                                    <div className="flex flex-wrap gap-2">
                                        {tags.map((tag, i) => (
                                            <span key={i} className={`inline-flex items-center gap-1.5 px-2.5 py-1 ${ch.bg} ${ch.text} rounded-full text-xs font-mono border ${ch.border}`}>
                                                {tag}
                                                <button onClick={() => setTags(tags.filter((_, j) => j !== i))} className="hover:opacity-70 transition">
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </span>
                                        ))}
                                        <input ref={tagRef} value={tagInput}
                                            onChange={e => setTagInput(e.target.value)}
                                            onKeyDown={handleTagKey} onPaste={handleTagPaste}
                                            placeholder={tags.length ? '' : 'Type or paste IDs separated by commas…'}
                                            className="flex-1 min-w-[200px] bg-transparent text-white placeholder-white/25 outline-none text-sm py-1" />
                                    </div>
                                </div>
                                <div className="mt-2 flex items-center justify-between text-xs">
                                    <span className={`font-medium ${tags.length > activeLimit ? 'text-red-400' : 'text-white/40'}`}>
                                        {tags.length.toLocaleString()} / {activeLimit.toLocaleString()} IDs
                                        {tags.length > activeLimit && ' — exceeds limit'}
                                    </span>
                                    {tags.length > 0 && (
                                        <button onClick={() => setTags([])} className="text-red-400 hover:text-red-300 flex items-center gap-1 transition">
                                            <Trash2 className="w-3 h-3" /> Clear all
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* EXCEL */}
                        {inputMode === 'excel' && (
                            <div className="space-y-4">
                                <div onClick={() => fileInputRef.current?.click()}
                                    className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${excelFile ? ch.border : 'border-white/20 hover:border-white/40'}`}
                                    style={{ background: excelFile ? undefined : 'rgba(255,255,255,0.03)' }}>
                                    <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFileChange} />
                                    {excelLoading ? (
                                        <div className="flex flex-col items-center gap-2">
                                            <Loader2 className="w-8 h-8 animate-spin text-white/50" />
                                            <p className="text-white/60 text-sm">Reading file…</p>
                                        </div>
                                    ) : excelFile ? (
                                        <div className="flex flex-col items-center gap-2">
                                            <FileSpreadsheet className={`w-10 h-10 ${ch.text}`} />
                                            <p className="text-white font-semibold">{excelFile.name}</p>
                                            <p className="text-white/40 text-xs">{(excelFile.size / 1024).toFixed(1)} KB · Click to change</p>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center gap-3">
                                            <Upload className="w-10 h-10 text-white/25" />
                                            <div>
                                                <p className="text-white/70 font-semibold">Drop your Excel / CSV here</p>
                                                <p className="text-white/40 text-sm mt-1">or click to browse — .xlsx, .xls, .csv</p>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {excelHeaders.length > 0 && !colConfirmed && (
                                    <div className="rounded-xl border border-white/15 overflow-hidden"
                                        style={{ background: 'rgba(255,255,255,0.05)' }}>
                                        <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
                                            <HelpCircle className={`w-4 h-4 ${ch.text}`} />
                                            <p className="text-white/80 text-sm font-semibold">Which column contains the IDs?</p>
                                        </div>
                                        <div className="p-4 space-y-3">
                                            <p className="text-white/40 text-xs">Select the column that holds the document IDs to search:</p>
                                            <div className="flex flex-wrap gap-2">
                                                {excelHeaders.map(h => (
                                                    <button key={h} onClick={() => handleColSelect(h)}
                                                        className={`px-3 py-2 rounded-lg text-sm font-mono transition-all border
                                                            ${excelCol === h ? `${ch.bg} ${ch.text} ${ch.border} font-bold` : 'text-white/50 border-white/10 hover:border-white/25 hover:text-white/80'}`}
                                                        style={excelCol !== h ? { background: 'rgba(255,255,255,0.06)' } : {}}>
                                                        <Columns className="inline w-3 h-3 mr-1.5 opacity-60" />
                                                        {h}
                                                    </button>
                                                ))}
                                            </div>
                                            {excelCol && (
                                                <div className="flex items-center gap-3 mt-2">
                                                    <p className="text-white/50 text-xs flex-1">
                                                        Column <span className={`font-mono font-bold ${ch.text}`}>"{excelCol}"</span> selected — click Confirm to extract IDs
                                                    </p>
                                                    <button onClick={confirmColumn}
                                                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r ${ch.color} hover:opacity-90 transition shadow-lg`}>
                                                        <CheckCircle className="w-4 h-4" /> Confirm Column
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {colConfirmed && excelIds.length > 0 && (
                                    <div className={`p-4 rounded-xl border ${excelIds.length > activeLimit ? 'border-amber-500/40' : ch.border}`}
                                        style={{ background: 'rgba(255,255,255,0.06)' }}>
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                {excelIds.length > activeLimit
                                                    ? <AlertCircle className="w-4 h-4 text-amber-300" />
                                                    : <CheckCircle className={`w-4 h-4 ${ch.text}`} />
                                                }
                                                <p className={`text-sm font-semibold ${excelIds.length > activeLimit ? 'text-amber-300' : ch.text}`}>
                                                    {excelIds.length.toLocaleString()} IDs extracted from{' '}
                                                    <span className="font-mono">"{excelCol}"</span>
                                                    {excelIds.length > activeLimit && (
                                                        <span className="ml-2 text-xs font-normal text-amber-200/70">
                                                            — exceeds {activeLimit.toLocaleString()} {deliveryMode === 'socket' ? 'stream' : 'API'} limit
                                                        </span>
                                                    )}
                                                </p>
                                            </div>
                                            <button onClick={() => { setColConfirmed(false); setExcelIds([]); }}
                                                className="text-white/30 hover:text-white/60 text-xs underline transition">
                                                Change column
                                            </button>
                                        </div>
                                        <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
                                            {excelIds.slice(0, 50).map((id, i) => (
                                                <span key={i} className="text-[11px] font-mono text-white/70 bg-white/10 border border-white/10 px-2 py-0.5 rounded-md">{id}</span>
                                            ))}
                                            {excelIds.length > 50 && (
                                                <span className="text-xs text-white/35 self-center">+{excelIds.length - 50} more…</span>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ERROR */}
                        {errorMsg && (
                            <div className="mt-4 p-3 rounded-lg border border-red-500/30 flex items-center gap-2"
                                style={{ background: 'rgba(239,68,68,0.12)' }}>
                                <AlertCircle className="w-4 h-4 text-red-300 shrink-0" />
                                <span className="text-red-200 text-sm">{errorMsg}</span>
                            </div>
                        )}

                        {/* ACTION BUTTONS */}
                        <div className="mt-5 flex items-center gap-3">
                            <button onClick={handleSearch} disabled={isLoading}
                                className={`flex-1 flex items-center justify-center gap-2 py-3 bg-gradient-to-r ${ch.color} text-white rounded-xl font-bold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition shadow-lg text-sm`}>
                                {isLoading
                                    ? <><Loader2 className="w-5 h-5 animate-spin" /> {deliveryMode === 'socket' ? 'Streaming…' : 'Searching…'}</>
                                    : <><Search className="w-5 h-5" /> Search {ch.label} Logs</>
                                }
                            </button>
                            <button onClick={resetAll} disabled={isLoading}
                                className="flex items-center gap-2 px-4 py-3 rounded-xl text-white/60 hover:text-white disabled:opacity-40 transition border border-white/15 text-sm font-medium"
                                style={{ background: 'rgba(255,255,255,0.07)' }}>
                                <RefreshCw className="w-4 h-4" /> Reset
                            </button>
                        </div>
                    </div>
                </div>

                {/* LOADING */}
                {isLoading && (
                    <div className="flex flex-col items-center justify-center py-16 gap-4">
                        <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${ch.color} flex items-center justify-center shadow-xl animate-pulse`}>
                            <Search className="w-6 h-6 text-white" />
                        </div>
                        <p className="text-white/70 font-medium text-sm">
                            {deliveryMode === 'socket' ? `Streaming ${ch.label} records…` : `Fetching ${ch.label} records…`}
                        </p>
                        {deliveryMode === 'socket' && (
                            <p className="text-white/30 text-xs">Waiting for server response…</p>
                        )}
                    </div>
                )}

                {/* EMPTY STATE */}
                {!isLoading && searched && results.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-16 gap-3">
                        <AlertCircle className="w-12 h-12 text-white/20" />
                        <p className="text-white/50 font-semibold">No records found</p>
                        <p className="text-white/30 text-sm">Try different IDs or switch channel</p>
                    </div>
                )}

                {/* RESULTS */}
                {!isLoading && results.length > 0 && (
                    <div>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-white font-bold text-lg flex items-center gap-2">
                                Results
                                <span className={`px-2.5 py-0.5 text-sm rounded-full font-semibold ${ch.bg} ${ch.text} border ${ch.border}`}>
                                    {results.length} record{results.length !== 1 ? 's' : ''}
                                </span>
                            </h2>
                        </div>
                        <div className="space-y-3">
                            {results.map((item, i) => (
                                <ResultCard key={item._id || item.id || i} item={item} channel={channel} onViewDetails={setSelectedItem} />
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {renderDetails()}
        </div>
    );
}